import Model from '@ember-data/model';
import { inject as service } from '@ember/service';
import attr from 'ember-data/attr';
import ApiService from 'cio/services/api';
import { isEmpty } from '@ember/utils';
import { waitForPromise } from '@ember/test-waiters';

export enum IdentityProvider {
  GoogleOIDC = 'google_oidc',
  OktaOIDC = 'okta_oidc',
  AzureOIDC = 'azure_oidc',
  GenericOIDC = 'generic_oidc',
}

type OpenIDConfigurationResponse = {
  authorization_endpoint: string;
  token_endpoint: string;
};

export default class SsoConfiguration extends Model {
  @service api!: ApiService;

  @attr('string') identityProvider!: IdentityProvider;

  // specific to Google OpenID Connect SSO:
  @attr('string') oidcGoogleHostedDomain!: string;

  // specific to Okta/generic OpenID Connect SSO:
  @attr('string') oidcClientId!: string;
  @attr('string') oidcClientSecret!: string;
  @attr('string') oidcIssuerUrl!: string;

  // OAuth authorization code and redirect URI:
  // not persisted or returned from API, only required for verification in POST call (creating new configuration):
  @attr('string') code!: string;
  @attr('string') redirectUri!: string;

  set oidcConfigurationUrl(url: string) {
    // Used for Azure:
    // "https://login.microsoftonline.com/abc-123/v2.0/.well-known/openid-configuration" ->
    // "https://login.microsoftonline.com/abc-123/v2.0"
    let openIDConfigSuffixRegex = /\/\.well-known\/openid-configuration$/;
    if (!openIDConfigSuffixRegex.test(url)) {
      this.set('oidcIssuerUrl', '');
      return;
    }

    this.set('oidcIssuerUrl', url.replace(openIDConfigSuffixRegex, ''));
  }
  get oidcConfigurationUrl() {
    if (isEmpty(this.oidcIssuerUrl)) {
      return '';
    }

    if (this.oidcIssuerUrl.endsWith('/')) {
      return `${this.oidcIssuerUrl}.well-known/openid-configuration`;
    }

    return `${this.oidcIssuerUrl}/.well-known/openid-configuration`;
  }

  async getOpenIDConfigurationInfo(): Promise<{ authorizationURL: string }> {
    let res = await waitForPromise(fetch(this.oidcConfigurationUrl)); // waitForPromise needed for tests
    let json: OpenIDConfigurationResponse = await res.json();

    return { authorizationURL: json.authorization_endpoint };
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'sso-configuration': SsoConfiguration;
  }
}
